import { z } from 'zod';
import { LEAVE_SESSIONS, LEAVE_TYPES, PAY_METHODS, PAYEE_KINDS, PAYMENT_DOCUMENTS, RECRUITMENT_REASONS, REQUEST_LIMITS, TOPUP_ACCOUNTS, TRIP_EXPENSE_MODES, TRIP_TRANSPORTS, } from '../requests.js';
/**
 * ĐƠN TỪ VÀ PHIẾU ĐỀ XUẤT (05/10/2026) — hợp đồng giữa web và máy chủ. Luật chuỗi duyệt, ai duyệt / thực hiện
 * được ở `requests.ts` (gói shared), hai bên cùng gọi.
 */
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ngày không hợp lệ');
const optionalDay = isoDay.or(z.literal('')).default('');
const clockTime = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Giờ phải theo dạng HH:MM')
    .or(z.literal(''))
    .default('');
const text = (max) => z.string().trim().max(max).default('');
const required = (max, message) => z.string().trim().min(1, message).max(max);
const money = z.number().int().min(0).max(REQUEST_LIMITS.amount);
const positiveMoney = money.min(1, 'Số tiền phải lớn hơn 0');
const lineList = (line) => z
    .array(line)
    .min(1, 'Cần ít nhất một dòng')
    .max(REQUEST_LIMITS.lines, `Tối đa ${REQUEST_LIMITS.lines} dòng`);
/** Một dòng «Nội dung mua hàng · Số lượng · Đơn giá» của đề nghị mua hàng. */
const purchaseLine = z.object({
    name: required(REQUEST_LIMITS.title, 'Dòng nào cũng cần nội dung'),
    quantity: z.number().min(0).max(1_000_000).default(1),
    price: money,
});
/** Một dòng «Nội dung · Số tiền · Ghi chú» của đề nghị thanh toán. */
const paymentLine = z.object({
    content: required(REQUEST_LIMITS.title, 'Dòng nào cũng cần nội dung'),
    amount: positiveMoney,
    note: text(REQUEST_LIMITS.title),
});
/** Thông tin nhận tiền: tiền mặt hay chuyển khoản (Tên TK · Số TK · Mã TK · Ngân hàng như mẫu giấy). */
const bankFields = {
    method: z.enum(PAY_METHODS).default('transfer'),
    accountName: text(120),
    accountNumber: text(40),
    accountCode: text(40),
    bank: text(120),
};
/** Một khoản «A. CHI PHÍ» của công tác phí: số lượng × đơn giá, kèm diễn giải. */
const EMPTY_COST = { quantity: 0, price: 0, note: '' };
const tripCost = z
    .object({
    quantity: z.number().min(0).max(100_000).default(0),
    price: money.default(0),
    note: text(REQUEST_LIMITS.title),
})
    .default(EMPTY_COST);
const tripCosts = z.object({
    room: tripCost,
    ticket: tripCost,
    taxi: tripCost,
    local: tripCost,
    living: tripCost,
    other: tripCost,
});
/** Một dòng «Diễn giải chi tiết khoản giao tế»: tên, chức vụ đối tác — công ty, số tiền, VAT. */
const tripGuest = z.object({
    name: required(REQUEST_LIMITS.title, 'Ghi tên, chức vụ, công ty của đối tác'),
    amount: money,
    vat: money.default(0),
});
/** Xin đi muộn MỘT ngày — gửi trước, hoặc tự sinh khi chấm muộn kèm lý do. */
export const lateRequestSchema = z.object({
    kind: z.literal('late'),
    day: isoDay,
    /** Giờ dự kiến tới (có thể bỏ trống). */
    arriveAt: clockTime,
    reason: required(REQUEST_LIMITS.reason, 'Chưa ghi lý do'),
});
/** Nghỉ phép: từ ngày — đến ngày, mỗi ngày cả ngày hoặc một buổi. */
export const leaveRequestSchema = z.object({
    kind: z.literal('leave'),
    leaveType: z.enum(LEAVE_TYPES),
    from: isoDay,
    to: isoDay,
    session: z.enum(LEAVE_SESSIONS),
    reason: required(REQUEST_LIMITS.reason, 'Chưa ghi lý do'),
});
export const recruitmentRequestSchema = z.object({
    kind: z.literal('recruitment'),
    position: required(REQUEST_LIMITS.title, 'Chưa ghi vị trí cần tuyển'),
    /** Phòng cần người — mặc định là phòng của người lập. */
    departmentId: text(64),
    quantity: z.number().int().min(1).max(REQUEST_LIMITS.recruitQuantity),
    reason: z.enum(RECRUITMENT_REASONS),
    description: text(REQUEST_LIMITS.content),
    requirements: text(REQUEST_LIMITS.content),
    /** Mức lương dự kiến, ghi tự do («12–15 triệu»). */
    salary: text(REQUEST_LIMITS.title),
    neededBy: optionalDay,
});
/** Đề nghị mua hàng (theo mẫu giấy): nội dung đề nghị và các dòng hàng (số lượng × đơn giá). */
export const purchaseRequestSchema = z.object({
    kind: z.literal('purchase'),
    title: required(REQUEST_LIMITS.title, 'Chưa ghi nội dung đề nghị'),
    lines: lineList(purchaseLine),
});
/** Đề xuất nạp tiền tài khoản (Like, AI, quảng cáo…). */
export const topupRequestSchema = z.object({
    kind: z.literal('topup'),
    account: z.enum(TOPUP_ACCOUNTS),
    /** Tên tài khoản / dịch vụ cần nạp. */
    service: required(REQUEST_LIMITS.title, 'Chưa ghi tài khoản cần nạp'),
    amount: positiveMoney,
    purpose: text(REQUEST_LIMITS.content),
});
/**
 * Giấy đề nghị tạm ứng — nội dung của mẫu 03-TT (Thông tư 99/2025/TT-BTC): số tiền, lý do tạm ứng, thời hạn
 * thanh toán (ngày hoàn lại số đã tạm ứng); kèm cách nhận tiền.
 */
export const advanceRequestSchema = z.object({
    kind: z.literal('advance'),
    reason: required(REQUEST_LIMITS.title, 'Chưa ghi lý do tạm ứng'),
    amount: positiveMoney,
    repayBy: optionalDay,
    ...bankFields,
});
/** Đề nghị thanh toán (theo mẫu giấy): các khoản, bộ phận chịu phí, số đã tạm ứng, giấy tờ kèm theo. */
export const paymentRequestSchema = z.object({
    kind: z.literal('payment'),
    payeeKind: z.enum(PAYEE_KINDS),
    lines: lineList(paymentLine),
    /** Mã phòng chịu phí; bỏ trống = phòng của người lập. */
    costCenter: text(64),
    /** «TẠM ỨNG · Ngày»: số đã tạm ứng trước, trừ vào tổng. */
    advanced: money.default(0),
    advanceDay: optionalDay,
    documents: z.array(z.enum(PAYMENT_DOCUMENTS)).max(PAYMENT_DOCUMENTS.length).default([]),
    ...bankFields,
});
/** Công tác phí (theo mẫu giấy): tạm ứng trước chuyến hoặc thanh toán sau chuyến. */
export const tripExpenseRequestSchema = z.object({
    kind: z.literal('trip-expense'),
    mode: z.enum(TRIP_EXPENSE_MODES),
    purpose: required(REQUEST_LIMITS.title, 'Chưa ghi mục đích chuyến công tác'),
    destination: required(REQUEST_LIMITS.title, 'Chưa ghi địa điểm'),
    from: isoDay,
    to: isoDay,
    costs: tripCosts,
    guests: z
        .array(tripGuest)
        .max(REQUEST_LIMITS.guests, `Tối đa ${REQUEST_LIMITS.guests} đối tác`)
        .default([]),
    /** «B. ĐÃ TẠM ỨNG». */
    advanced: money.default(0),
    /** Chủ thẻ · Số thẻ · Tại ngân hàng. */
    accountName: text(120),
    accountNumber: text(40),
    bank: text(120),
});
/** Lịch công tác: ai đi, đi đâu, bao giờ, bằng gì. */
export const tripRequestSchema = z.object({
    kind: z.literal('trip'),
    title: required(REQUEST_LIMITS.title, 'Chưa ghi nội dung chuyến đi'),
    destination: required(REQUEST_LIMITS.title, 'Chưa ghi nơi đến'),
    /** Tên đăng nhập những người đi. */
    travellers: z.array(z.string().trim().min(1).max(64)).max(REQUEST_LIMITS.travellers).default([]),
    departDay: isoDay,
    departTime: clockTime,
    returnDay: isoDay,
    returnTime: clockTime,
    transport: z.enum(TRIP_TRANSPORTS),
    note: text(REQUEST_LIMITS.content),
});
export const createRequestSchema = z.discriminatedUnion('kind', [
    lateRequestSchema,
    leaveRequestSchema,
    recruitmentRequestSchema,
    purchaseRequestSchema,
    topupRequestSchema,
    advanceRequestSchema,
    paymentRequestSchema,
    tripExpenseRequestSchema,
    tripRequestSchema,
]);
/** Duyệt / không duyệt bước đang chờ. */
export const decideRequestSchema = z.object({
    decision: z.enum(['approved', 'rejected']),
    comment: text(REQUEST_LIMITS.comment),
});
/** Hành chính / kế toán đánh dấu đã thực hiện, kèm ghi chú (số phiếu chi, ngày nhận việc…). */
export const executeRequestSchema = z.object({ note: text(REQUEST_LIMITS.comment) });
/** Huỷ: người lập rút phiếu còn chờ; Giám đốc huỷ phiếu chờ / đã duyệt. Kèm lý do (không bắt buộc). */
export const cancelRequestSchema = z.object({ reason: text(REQUEST_LIMITS.comment) });
/** `?box=mine` phiếu của tôi · `all` mọi phiếu người xem được thấy (phạm vi, chờ mình, mình thực hiện). */
export const REQUEST_BOXES = ['mine', 'all'];
export const listRequestsQuerySchema = z.object({
    box: z.preprocess((value) => (Array.isArray(value) ? value[0] : value), z.enum(REQUEST_BOXES).default('all')),
});
//# sourceMappingURL=requests.js.map